import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getLinks, createLink, updateLink, deleteLink } from '../api/links';

function AdminPage() {
  const [links, setLinks] = useState([]);
  const [password, setPassword] = useState('');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  
  const [editingLink, setEditingLink] = useState(null);
  const [draggedLinkId, setDraggedLinkId] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    url: '',
  });

  useEffect(() => {
    if (isAuthenticated) {
      fetchLinks();
    }
  }, [isAuthenticated]);

  const fetchLinks = async () => {
    try {
      const data = await getLinks();
      setLinks(data);
    } catch (error) {
      console.error('Failed to fetch links:', error);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await createLink(
        { title: '__test__', url: 'https://test.com', display_order: -1 },
        password
      ).then(async (newLink) => {
        await deleteLink(newLink.id, password);
      });
      setIsAuthenticated(true);
    } catch (err) {
      if (err.response?.status === 401) {
        setError('Invalid password');
      } else {
        setError('An error occurred');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    try {
      if (editingLink) {
        await updateLink(editingLink.id, formData, password);
      } else {
        await createLink({ ...formData, display_order: links.length }, password);
      }
      await fetchLinks();
      resetForm();
    } catch (err) {
      setError(err.response?.data?.detail || 'An error occurred');
    }
  };

  const handleEdit = (link) => {
    setEditingLink(link);
    setFormData({
      title: link.title,
      url: link.url,
    });
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this link?')) {
      return;
    }

    try {
      await deleteLink(id, password);
      await fetchLinks();
    } catch (err) {
      setError(err.response?.data?.detail || 'An error occurred');
    }
  };

  const resetForm = () => {
    setEditingLink(null);
    setFormData({ title: '', url: '' });
  };

  const handleDragStart = (linkId) => {
    setDraggedLinkId(linkId);
  };

  const handleDragEnd = () => {
    setDraggedLinkId(null);
  };

  const handleDragOver = (event) => {
    event.preventDefault();
  };

  const handleDrop = async (targetLinkId) => {
    if (!draggedLinkId || draggedLinkId === targetLinkId) {
      return;
    }

    const previousLinks = [...links];
    const reorderedLinks = [...links];
    const fromIndex = reorderedLinks.findIndex((link) => link.id === draggedLinkId);
    const toIndex = reorderedLinks.findIndex((link) => link.id === targetLinkId);

    if (fromIndex === -1 || toIndex === -1) {
      setDraggedLinkId(null);
      return;
    }

    const [movedLink] = reorderedLinks.splice(fromIndex, 1);
    reorderedLinks.splice(toIndex, 0, movedLink);

    const withUpdatedOrder = reorderedLinks.map((link, index) => ({
      ...link,
      display_order: index,
    }));

    setLinks(withUpdatedOrder);
    setDraggedLinkId(null);

    try {
      await Promise.all(
        withUpdatedOrder.map((link) =>
          updateLink(link.id, { display_order: link.display_order }, password)
        )
      );
      await fetchLinks();
    } catch (err) {
      setLinks(previousLinks);
      setError(err.response?.data?.detail || 'Failed to reorder links');
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="admin-page">
        <div className="admin-container">
          <div className="login-section">
            <h2>Admin Login</h2>
            <form onSubmit={handleLogin}>
              <input
                type="password"
                className="password-input"
                placeholder="Enter admin password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
              <br />
              <button type="submit" className="login-button" disabled={loading}>
                {loading ? 'Verifying...' : 'Login'}
              </button>
            </form>
            {error && <p className="error-message">{error}</p>}
            <div style={{ marginTop: '20px' }}>
              <Link to="/" className="back-link">← Back to Links</Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-page">
      <div className="admin-container">
        <div className="admin-header">
          <h1 className="admin-title">Manage Links</h1>
          <Link to="/" className="back-link">← Back to Links</Link>
        </div>

        <div className="link-form">
          <h3 className="form-title">
            {editingLink ? 'Edit Link' : 'Add New Link'}
          </h3>
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label>Title</label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="My Website"
                required
              />
            </div>
            <div className="form-group">
              <label>URL</label>
              <input
                type="url"
                value={formData.url}
                onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                placeholder="https://example.com"
                required
              />
            </div>
            {error && <p className="error-message">{error}</p>}
            <div className="form-actions">
              <button type="submit" className="btn btn-primary">
                {editingLink ? 'Update Link' : 'Add Link'}
              </button>
              {editingLink && (
                <button type="button" className="btn btn-secondary" onClick={resetForm}>
                  Cancel
                </button>
              )}
            </div>
          </form>
        </div>

        <div className="links-list">
          {links.length === 0 ? (
            <div className="empty-state">
              <p>No links yet. Add your first link above!</p>
            </div>
          ) : (
            links.map((link) => (
              <div
                key={link.id}
                className={`link-item ${draggedLinkId === link.id ? 'dragging' : ''}`}
                draggable
                onDragStart={() => handleDragStart(link.id)}
                onDragEnd={handleDragEnd}
                onDragOver={handleDragOver}
                onDrop={() => handleDrop(link.id)}
              >
                <div className="drag-handle" aria-hidden="true">☰</div>
                <div className="link-order">{link.display_order}</div>
                <div className="link-info">
                  <div className="link-title">{link.title}</div>
                  <div className="link-url">{link.url}</div>
                </div>
                <div className="link-actions">
                  <button
                    className="btn btn-secondary btn-small"
                    onClick={() => handleEdit(link)}
                  >
                    Edit
                  </button>
                  <button
                    className="btn btn-danger btn-small"
                    onClick={() => handleDelete(link.id)}
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

export default AdminPage;
